import { CertificationRecord } from "./certificateTypes";

type CertInput = Omit<CertificationRecord, "scope">;

const employee = (cert: CertInput): CertificationRecord => ({
  ...cert,
  scope: "employee",
});

/** Employee training completion certificates — sales collateral names/taglines. */
export const EMPLOYEE_CERTIFICATIONS: CertificationRecord[] = [
  employee({
    id: "archie-securepath-personal-protection",
    title: "Archie+ SecurePath",
    subtitle: "REAL PERSON, REAL TIME, REAL PROTECTION.",
    description:
      "Real person, real time, real protection — safe-route guidance, check-ins, zone alerts, and escalation for the workforce.",
    recognitionText:
      "for completing Archie+ SecurePath training and demonstrating use of safe-route guidance, check-ins, zone alerts, and security or 911 escalation.",
    themeColor: "#A974FF",
    emoji: "🛡️",
    iconSuggestion: "Shield with route path",
    onmywayFeatures: ["Archie+ SecurePath™", "Safe-route guidance", "911 escalation"],
    criteria: [
      "Complete Archie+ SecurePath training module",
      "Configure check-in and zone alert preferences",
      "Pass escalation workflow knowledge check",
    ],
    certificateType: "training",
    issueDate: "Upon successful completion",
    expirationDate: "12 months from issue date",
  }),
  employee({
    id: "workplace-emergency-panic-response",
    title: "Workplace Emergency Dispatch",
    subtitle: "QUICK RESPONSE WHEN EMPLOYEES NEED HELP.",
    description:
      "Quick response when employees need help — panic workflows, threat detection, and emergency routing to security or 911.",
    recognitionText:
      "for completing Workplace Emergency Dispatch training on panic workflows, threat detection, and emergency response procedures.",
    themeColor: "#DC2626",
    emoji: "🚨",
    iconSuggestion: "Emergency alert",
    onmywayFeatures: [
      "Workplace Emergency Program™",
      "Panic workflows",
      "Security/911 routing",
    ],
    criteria: [
      "Complete Workplace Emergency Dispatch module",
      "Review site-specific emergency procedures",
      "Pass panic workflow knowledge validation",
    ],
    certificateType: "training",
    issueDate: "Upon successful completion",
    expirationDate: "12 months from issue date",
  }),
  employee({
    id: "ridewatch-transportation-safety",
    title: "RideWatch / Travel Protection Program",
    subtitle:
      "MONITORED WITH REAL PEOPLE, DOCUMENTED, AND BACKED BY SAFETY PROTOCOLS.",
    description:
      "Monitored with real people, documented, and backed by safety protocols for business travel and rideshare trips.",
    recognitionText:
      "for completing Rideshare RideWatch / Travel Protection Program training and demonstrating rideshare monitoring and evidence capture.",
    themeColor: "#3FA9FF",
    emoji: "🚕",
    iconSuggestion: "Monitored vehicle",
    onmywayFeatures: [
      "Rideshare RideWatch™",
      "Travel protection",
      "Evidence capture",
    ],
    criteria: [
      "Complete travel protection safety module",
      "Review trip monitoring and escalation procedures",
      "Pass panic and evidence workflow drill",
    ],
    certificateType: "training",
    issueDate: "Upon successful completion",
    expirationDate: "12 months from issue date",
  }),
  employee({
    id: "mental-health-workplace-wellness",
    title: "Mental Health Shield Program",
    subtitle: "SUPPORT WHEN STRESS OR CRISIS REQUIRES HELP OR IMMEDIATE DISPATCH.",
    description:
      "Support when stress or crisis requires help or immediate dispatch — confidential escalation and wellness resources.",
    recognitionText:
      "for completing Mental Health Shield Program training and engaging with approved workplace wellness and escalation resources.",
    themeColor: "#E5484D",
    emoji: "❤️",
    iconSuggestion: "Heart shield",
    onmywayFeatures: [
      "Mental Health Shield™",
      "Crisis dispatch",
      "Confidential escalation",
    ],
    criteria: [
      "Complete Mental Health Shield Program workshop",
      "Review wellness and escalation resources",
      "Acknowledge confidentiality pathways",
    ],
    certificateType: "training",
    issueDate: "Upon successful completion",
    expirationDate: "24 months from issue date",
  }),
  employee({
    id: "personal-safety-travel-concierge",
    title: "Employee Violence Response System",
    subtitle: "FAST, SAFE RESPONSE TO THREATENING SITUATIONS.",
    description:
      "Fast, safe response to threatening situations — documented threat response and security escalation workflows.",
    recognitionText:
      "for completing Employee Violence Response System training and demonstrating threat response and escalation procedures.",
    themeColor: "#B91C1C",
    emoji: "🛡️",
    iconSuggestion: "Violence response shield",
    onmywayFeatures: [
      "Threat response",
      "Security escalation",
      "Evidence documentation",
    ],
    criteria: [
      "Complete Employee Violence Response System module",
      "Review threat response protocols",
      "Pass escalation workflow validation",
    ],
    certificateType: "training",
    issueDate: "Upon successful completion",
    expirationDate: "12 months from issue date",
  }),
  employee({
    id: "lone-worker-remote-protection",
    title: "Remote / Lone Worker Lifeline",
    subtitle:
      "PROTECTION FOR SALES / REMOTE WORKERS AND EMPLOYEES WORKING ALONE.",
    description:
      "Protection for sales, remote workers, and employees working alone — check-ins, SOS workflows, and location-aware escalation.",
    recognitionText:
      "for completing Remote / Lone Worker Lifeline training and demonstrating lone worker check-ins, SOS workflows, and location-aware protection.",
    themeColor: "#FF8A3D",
    emoji: "👤",
    iconSuggestion: "Lone worker check-in",
    onmywayFeatures: [
      "Remote-Worker Lifeline™",
      "SOS timers",
      "Location-aware workflows",
    ],
    criteria: [
      "Complete Remote / Lone Worker Lifeline module",
      "Enable and test check-in and SOS workflows",
      "Acknowledge escalation procedures",
    ],
    certificateType: "training",
    issueDate: "Upon successful completion",
    expirationDate: "12 months from issue date",
  }),
  employee({
    id: "safety-in-motion-protection",
    title: "Safety In-Motion Program",
    subtitle: "PROTECTION FROM SLIPS AND FALLS, AI + REAL PERSON DISPATCH",
    description:
      "Protection from slips and falls with AI detection and real person dispatch — hazard reporting and incident documentation.",
    recognitionText:
      "for completing Safety In-Motion Program training on fall prevention, motion alerts, hazard reporting, and incident documentation.",
    themeColor: "#7CC242",
    emoji: "👷",
    iconSuggestion: "Hard hat with mobile report",
    onmywayFeatures: [
      "Safety In-Motion™",
      "Fall detection",
      "OSHA incident trails",
    ],
    criteria: [
      "Complete Safety In-Motion Program training module",
      "Acknowledge motion alert and escalation settings",
      "Pass hazard reporting and prevention assessment",
    ],
    certificateType: "training",
    issueDate: "Upon successful completion",
    expirationDate: "12 months from issue date",
  }),
  employee({
    id: "defensive-driving-distracted",
    title: "OnMyWay Drive Program",
    subtitle:
      "DISTRACTED DRIVING PROTECTION FOR ALL EMPLOYEES ANYTIME THEY GET BEHIND THE WHEEL.",
    description:
      "Distracted driving protection for all employees anytime they get behind the wheel — drive mode and safe driving documentation.",
    recognitionText:
      "for completing OnMyWay Drive Program training on distracted driving prevention and demonstrating use of drive mode and safe driving documentation.",
    themeColor: "#3FA9FF",
    emoji: "🚗",
    iconSuggestion: "Vehicle with shield",
    onmywayFeatures: [
      "OnMyWay Drive™",
      "Drive mode",
      "Safe driving documentation",
    ],
    criteria: [
      "Complete OnMyWay Drive Program safety module",
      "Enable drive mode where assigned",
      "Pass distracted driving assessment",
    ],
    certificateType: "training",
    issueDate: "Upon successful completion",
    expirationDate: "12 months from issue date",
  }),
  employee({
    id: "safety-engagement-participation",
    title: "OnMyWay One – Reporting and Repository",
    subtitle: "SIMPLE REPORTING / SAFETY PROGRAM AND COMPLIANCE CERTIFICATION.",
    description:
      "Simple reporting, safety program, and compliance certification — participation in OnMyWay One reporting and certification workflows.",
    recognitionText:
      "for demonstrating active participation in OnMyWay One reporting, safety program, and compliance certification requirements.",
    themeColor: "#38BDF8",
    emoji: "📅",
    iconSuggestion: "Reporting repository",
    onmywayFeatures: [
      "OnMyWay One reporting",
      "Certification repository",
      "Compliance dashboards",
    ],
    criteria: [
      "Complete OnMyWay One reporting orientation",
      "Meet minimum safety participation threshold",
      "Maintain active reporting participation for 30 days",
    ],
    certificateType: "training",
    issueDate: "Upon successful completion",
    expirationDate: "12 months from issue date",
  }),
  employee({
    id: "verifyai-digital-identity-protection",
    title: "VerifyAI Cybersecurity",
    subtitle:
      "IDENTIFY MANIPULATED MEDIA AND DIGITAL IMPERSONATION AND RISKS THAT ARE ATTACHED.",
    description:
      "Identify manipulated media, digital impersonation, and attached risks — deepfake detection and fraud-aware decision workflows.",
    recognitionText:
      "for completing VerifyAI Cybersecurity training on deepfake detection, identity verification, impersonation prevention, and fraud-aware decision workflows.",
    themeColor: "#2BD4C8",
    emoji: "🔒",
    iconSuggestion: "Identity scan shield",
    onmywayFeatures: [
      "VerifyAI™",
      "Deepfake detection",
      "Impersonation prevention",
    ],
    criteria: [
      "Complete VerifyAI Cybersecurity training module",
      "Pass deepfake and impersonation scenario assessment",
      "Acknowledge high-risk verification procedures",
    ],
    certificateType: "training",
    issueDate: "Upon successful completion",
    expirationDate: "12 months from issue date",
  }),
  employee({
    id: "safety-policy-acknowledgement",
    title: "Company Safety Policy Acknowledgement",
    subtitle: "EMPLOYER POLICY ATTESTATION",
    description:
      "Formal acknowledgement of the company safety policy published through the OnMyWay Employer Dashboard.",
    recognitionText:
      "for reviewing and acknowledging the company safety policy and assigned OnMyWay protection platform requirements.",
    themeColor: "#6366F1",
    emoji: "✅",
    iconSuggestion: "Policy checkmark",
    onmywayFeatures: [
      "Company Safety Policy",
      "Employer Dashboard",
      "Policy PDF export",
    ],
    criteria: [
      "Review current company safety policy",
      "Complete policy acknowledgement attestation",
      "Confirm understanding of reporting process",
    ],
    certificateType: "training",
    issueDate: "Upon policy acknowledgement",
    expirationDate: "12 months from issue date or upon policy revision",
  }),
];

export const SAFETY_INITIATIVES = EMPLOYEE_CERTIFICATIONS;
